import React, { useState } from 'react';
import { Form, Button, Alert, Spinner, InputGroup, Dropdown, DropdownButton } from 'react-bootstrap';
import { register } from '../services/api';
import { validateEmail } from '../pages/validation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import '../styles/Style.css';

function Register() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [countryCode, setCountryCode] = useState('+1');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [phoneNumberError, setPhoneNumberError] = useState('');
  const [nameError, setNameError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const [apiError, setApiError] = useState();
  const [apiSuccess, setApiSuccess] = useState();
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaError, setCaptchaError] = useState('');

  const handleRegister = async () => {
    setIsLoading(true);
    setEmailError('');
    setPasswordError('');
    setConfirmPasswordError('');
    setPhoneNumberError('');
    setNameError('');
    setCaptchaError('');

    if (name.trim() === '') {
      setNameError('Name is required.');
      setIsLoading(false);
      return;
    }

    const emailValidationError = validateEmail(email);
    if (emailValidationError) {
      setEmailError(emailValidationError);
      setIsLoading(false);
      return;
    }

    if (password.length < 6) {
      setPasswordError('Password should be at least 6 characters long.');
      setIsLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setConfirmPasswordError('Passwords do not match.');
      setIsLoading(false);
      return;
    }

    if (!captchaToken) {
      setCaptchaError('Please complete the CAPTCHA.');
      setIsLoading(false);
      return;
    }

    try {
      const data = await register(name, email, password, phoneNumber, countryCode);
      setApiSuccess(data.msg);
      setIsLoading(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      if (error.response?.data?.error?.email) {
        setApiError(error.response.data.error.email);
      } else {
        setApiError('Registration failed. Please try again.');
      }
      setIsLoading(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const onCaptchaChange = (token) => {
    setCaptchaToken(token);
    setCaptchaError('');
  };

  const countryCodes = [
    { name: 'Pakistan', code: '+92' },
    { name: 'United States', code: '+1' },
    { name: 'India', code: '+91' },
    { name: 'United Kingdom', code: '+44' },
    { name: 'Canada', code: '+1' },
    { name: 'Australia', code: '+61' },
  ];

  return (
    <div className="d-flex flex-column flex-lg-row vh-120">
      {/* Left Side with Background */}
      <div
        className="d-flex flex-column justify-content-center align-items-center text-white w-100 w-lg-50 px-4 px-md-5"
        style={{
          backgroundImage: 'url(/images/login.png)', // Replace with your actual path
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="text-center">
          <Link to="/">
            <img
              src="/images/white-logo.png"
              alt="Logo"
              style={{ width: 300, marginBottom: 10, cursor: 'pointer' }}
            />
          </Link>
        </div>
      </div>

      {/* Right Side Form */}
      <div className="d-flex flex-column justify-content-center w-100 w-lg-50 px-4 px-md-5">
        <div style={{ maxWidth: '400px', width: '100%', margin: '0 auto' }}>
          <h3 className="fw-bold mb-3">Create your account</h3>
          <p className="text-muted mb-4">Fill in the details below to get started</p>

          {apiError && (
            <Alert variant="danger" dismissible onClose={() => setApiError('')}>
              {apiError} <Link to="/login" className="text-decoration-none">Log In</Link>
            </Alert>
          )}
          {apiSuccess && (
            <Alert variant="success" dismissible onClose={() => setApiSuccess('')}>
              {apiSuccess} <Link to="/login" className="text-decoration-none">Log In</Link>
            </Alert>
          )}

          <Form>
            {/* Name */}
            <Form.Group controlId="formBasicName">
              <Form.Control
                type="text"
                placeholder="Enter your name"
                value={name}
                isInvalid={!!nameError}
                onChange={(e) => setName(e.target.value)}
              />
              <Form.Control.Feedback type="invalid">{nameError}</Form.Control.Feedback>
            </Form.Group>

            {/* Email */}
            <Form.Group controlId="formBasicEmail" className="mt-3">
              <Form.Control
                type="email"
                placeholder="Enter your email"
                value={email}
                isInvalid={!!emailError}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (emailError && !validateEmail(e.target.value)) setEmailError('');
                }}
              />
              <Form.Control.Feedback type="invalid">{emailError}</Form.Control.Feedback>
            </Form.Group>

            {/* Password */}
            <Form.Group controlId="formBasicPassword" className="mt-3">
              <InputGroup>
                <Form.Control
                  type={passwordVisible ? 'text' : 'password'}
                  placeholder="Create a password"
                  value={password}
                  isInvalid={!!passwordError}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setPasswordError(e.target.value.length < 6 ? 'Password should be at least 6 characters long.' : '');
                  }}
                />
                <InputGroup.Text onClick={() => setPasswordVisible(!passwordVisible)} style={{ cursor: 'pointer' }}>
                  <FontAwesomeIcon icon={passwordVisible ? faEyeSlash : faEye} />
                </InputGroup.Text>
              </InputGroup>
              <Form.Control.Feedback type="invalid">{passwordError}</Form.Control.Feedback>
            </Form.Group>

            {/* Confirm Password */}
            <Form.Group controlId="formConfirmPassword" className="mt-3">
              <InputGroup>
                <Form.Control
                  type={confirmPasswordVisible ? 'text' : 'password'}
                  placeholder="Confirm your password"
                  value={confirmPassword}
                  isInvalid={!!confirmPasswordError}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    setConfirmPasswordError(password !== e.target.value ? 'Passwords do not match.' : '');
                  }}
                />
                <InputGroup.Text onClick={() => setConfirmPasswordVisible(!confirmPasswordVisible)} style={{ cursor: 'pointer' }}>
                  <FontAwesomeIcon icon={confirmPasswordVisible ? faEyeSlash : faEye} />
                </InputGroup.Text>
              </InputGroup>
              <Form.Control.Feedback type="invalid">{confirmPasswordError}</Form.Control.Feedback>
            </Form.Group>

            {/* Phone */}
            <Form.Group controlId="formPhoneNumber" className="mt-3">
              <InputGroup>
                <DropdownButton
                  variant="outline-secondary"
                  title={countryCode}
                  onSelect={(selectedCode) => setCountryCode(selectedCode)}
                >
                  {countryCodes.map((country) => (
                    <Dropdown.Item key={country.name} eventKey={country.code}>
                      {country.name} ({country.code})
                    </Dropdown.Item>
                  ))}
                </DropdownButton>
                <Form.Control
                  type="text"
                  placeholder="Enter your phone number"
                  value={phoneNumber}
                  isInvalid={!!phoneNumberError}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                />
              </InputGroup>
              <Form.Control.Feedback type="invalid">{phoneNumberError}</Form.Control.Feedback>
            </Form.Group>

            {/* CAPTCHA */}
            <div className="captcha-section mt-4">
              <ReCAPTCHA
                sitekey="6LeDjVsqAAAAAHRCV2WBxU3v_Y55dsoFGvfFquA9"
                onChange={onCaptchaChange}
              />
              {captchaError && <div className="text-danger mt-2">{captchaError}</div>}
            </div>

            {/* Submit */}
            <Button className="w-100 mt-4" variant="primary" onClick={handleRegister} disabled={isLoading}>
              {isLoading ? <Spinner animation="border" size="sm" /> : 'Create Account'}
            </Button>

            <div className="text-center text-muted my-3">Or continue with</div>
            <div className="d-flex justify-content-between gap-2">
              <Button variant="light" className="w-100 border"><i className="fab fa-google"></i> G</Button>
              <Button variant="light" className="w-100 border"><i className="fab fa-apple"></i> </Button>
              <Button variant="light" className="w-100 border"><i className="fab fa-facebook-f"></i> f</Button>
            </div>

            <div className="text-center mt-3">
              Already have an account?{' '}
              <Link to="/login" className="fw-bold text-decoration-none">Sign In</Link>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
}

export default Register;
